import * as React from 'react';
import { X, Hash, Magnet } from 'lucide-react';
import { ViewSettings } from '@shared/types/tsk';

interface GridSettingsPanelProps {
  settings: ViewSettings;
  setSettings: (settings: ViewSettings) => void;
  onClose: () => void;
}

const GridSettingsPanel: React.FC<GridSettingsPanelProps> = ({ settings, setSettings, onClose }) => {
  const toggle = (key: keyof ViewSettings) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
  };

  // Map GridSize (pixels) to a Density value (10-100)
  // gridSize 100 (sparse) -> density 10
  // gridSize 20 (default) -> density 50 (middle)
  // gridSize 10 (dense) -> density 100
  const densityValue = Math.round(1000 / settings.gridSize);

  return (
    <div
      className="absolute top-20 left-20 z-[50] w-[240px] bg-slate-900/95 backdrop-blur-xl border border-white/20 rounded-[2rem] shadow-2xl overflow-hidden animate-in slide-in-from-top-2 duration-200 pointer-events-auto ring-1 ring-black"
      onPointerDown={stopPropagation}
    >
      <div className="px-5 py-3.5 border-b border-white/5 flex justify-between items-center bg-gradient-to-r from-emerald-600/20 to-transparent">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-emerald-600 text-white rounded-lg">
            <Hash size={14} />
          </div>
          <div>
            <h2 className="text-[10px] font-black uppercase tracking-[0.15em] text-white">Grid Config</h2>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 text-white/40 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        <div className="space-y-2">
          <SettingToggle
            icon={<Hash size={14} />}
            label="Show Grid"
            active={settings.showGrid}
            onClick={() => toggle('showGrid')}
          />
          <SettingToggle
            icon={<Magnet size={14} />}
            label="Snap to Grid"
            active={settings.snapToGrid}
            onClick={() => toggle('snapToGrid')}
          />
        </div>

        {/* Grid Density Slider */}
        <div className="pt-2 px-1">
          <div className="flex justify-between items-center mb-2">
            <label className="text-[9px] font-black text-white/30 uppercase tracking-widest">Grid Density</label>
            <span className="text-[9px] font-black text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-md">
              {settings.gridSize}px
            </span>
          </div>
          <input
            type="range"
            min="10"
            max="100"
            step="1"
            value={densityValue}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              // Inverse mapping back to pixels: 1000 / density
              setSettings({ ...settings, gridSize: Math.round(1000 / val) });
            }}
            className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
          />
          <div className="flex justify-between mt-1 px-0.5">
            <span className="text-[7px] font-black text-white/20 uppercase">Sparse</span>
            <span className="text-[7px] font-black text-white/20 uppercase">Dense</span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SettingToggleProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

const SettingToggle: React.FC<SettingToggleProps> = ({ icon, label, active, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all border ${active ? 'bg-emerald-600/10 border-emerald-500/30' : 'bg-white/5 border-transparent hover:bg-white/10'}`}
    >
      <div className="flex items-center gap-3">
        <div className={`${active ? 'text-emerald-400' : 'text-slate-500'}`}>
          {icon}
        </div>
        <span className={`text-[10px] font-black uppercase tracking-wider ${active ? 'text-white' : 'text-slate-400'}`}>{label}</span>
      </div>
      <div className={`w-7 h-3.5 rounded-full relative transition-colors ${active ? 'bg-emerald-500' : 'bg-slate-700'}`}>
        <div className={`absolute top-0.5 w-2.5 h-2.5 bg-white rounded-full transition-all ${active ? 'right-0.5' : 'left-0.5'}`} />
      </div>
    </button>
  );
};

export default GridSettingsPanel;